import { isTuningTelemetry } from '@playcastdotio/PeerCore';
import type { IExperimentRunner, IFreshStreamTrials, ITuningEndpoint, TuningCommand, TuningExperimentRequest,
  TuningExperimentSnapshot, TuningIdentity, TuningReceipt, TuningTelemetryWindow, TuningTrialSnapshot } from '@playcastdotio/PeerCore';
import type { HostTelemetryListener, IHostTuningControl, StreamTuningDependencies } from './HostTuningContracts';
import { HostTuningSessionManager } from './managers/HostTuningSessionManager';
import { HostTuningEventManager } from './managers/HostTuningEventManager';

/** Host session authority and bounded local discovery, with lifecycle/experiments injected. */
export class StreamTuningCoordinator implements IHostTuningControl {
  private readonly state: HostTuningSessionManager;
  private readonly history = new HostTuningEventManager();
  private readonly endpoints = new Map<string, ITuningEndpoint>();
  private readonly listeners = new Set<HostTelemetryListener>();
  private readonly running = new Set<string>();
  private readonly accepted = new Set<string>();
  private readonly owners = new Map<string, { id: string; deadlineMs: number; timer: ReturnType<typeof setTimeout> }>();
  private runner?: IExperimentRunner; private fresh?: IFreshStreamTrials;
  private readonly sessionFresh = new Map<string, IFreshStreamTrials>();
  private readonly revocations = new Map<string, Promise<void>>();
  constructor(private readonly deps: StreamTuningDependencies) {
    this.runner = deps.experimentRunner; this.fresh = deps.freshTrials;
    this.state = new HostTuningSessionManager({ ...deps, expire: id => this.revoke(id, 'lease-expired') });
    this.history.subscribe(event => deps.localTransport.publish(event)); deps.localTransport.bind(this);
  }
  public registerExperimentRunner(runner: IExperimentRunner): void { this.runner = runner; }
  public registerFreshStreamTrials(trials: IFreshStreamTrials, sessionId?: string): void {
    if (sessionId) this.sessionFresh.set(sessionId, trials); else this.fresh = trials;
  }
  public registerEndpoint(target: TuningIdentity, endpoint: ITuningEndpoint): void {
    this.endpoints.set(`${target.sessionId}:${endpoint.capabilities().endpoint}`, endpoint);
    if (endpoint.capabilities().endpoint === 'host' || !this.state.session(target.sessionId)) this.recordTrial(endpoint.snapshot());
  }
  public async removeEndpoint(target: TuningIdentity): Promise<void> {
    const owner = this.owners.get(target.sessionId);
    if (owner) { void this.runner?.cancel(owner.id, 'session-ended'); this.release(target.sessionId, owner.id); }
    for (const [key, endpoint] of this.endpoints) if (key.startsWith(`${target.sessionId}:`)) {
      endpoint.dispose(); this.endpoints.delete(key);
    }
    this.state.remove(target.sessionId); await this.deps.runtime.dispose(target);
    this.sessionFresh.delete(target.sessionId);
  }
  public recordTrial(snapshot: TuningTrialSnapshot): void {
    this.state.record(snapshot); this.history.append(snapshot.target, 'host', 'trial', snapshot);
  }
  public ingest(window: TuningTelemetryWindow): void {
    const current = this.state.session(window.target.sessionId)?.target;
    if (!isTuningTelemetry(window) || !current || current.peerId !== window.target.peerId
      || current.connectionEpoch !== window.target.connectionEpoch || current.trialId !== window.target.trialId
      || current.configRevision !== window.target.configRevision) return;
    this.history.append(window.target, window.endpoint, 'telemetry', window);
    for (const listener of this.listeners) listener(window);
  }
  public subscribe(listener: HostTelemetryListener): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  public subscribeEvents(listener: Parameters<HostTuningEventManager['subscribe']>[0]): () => void { return this.history.subscribe(listener); }
  public capabilities(sessionId?: string) {
    const snapshot = sessionId ? this.state.session(sessionId) : this.state.sessions()[0];
    const host = this.deps.runtime.capabilities(snapshot?.target ?? { sessionId: 'discovery', trialId: 'discovery',
      peerId: 'discovery', connectionEpoch: 0, configRevision: 0 });
    return { endpoints: [{ ...host, armed: !!sessionId && !!this.state.lease(sessionId) },
      ...[...this.endpoints].filter(([key]) => key === `${sessionId}:guest`).map(([, endpoint]) => endpoint.capabilities())] };
  }
  public sessions() { return this.state.sessions(); }
  public authorized(command: TuningCommand): boolean { return this.state.authorized(command); }
  public hasLease(sessionId: string, leaseId: string): boolean {
    const lease = this.state.lease(sessionId);
    return !!lease && lease.leaseId === leaseId && lease.expiresAtHostMonotonicMs > this.deps.clock.monotonicMs();
  }
  public session(id: string) { return this.state.session(id); }
  public trial(id: string) { return this.state.trial(id); }
  public events(id: string, after: number, limit: number, trialId?: string) { return this.history.page(id, after, limit, trialId); }
  public async arm(id: string, ttlMs: number) {
    if (this.revocations.has(id)) throw new Error('operation-in-progress');
    const lease = this.state.arm(id, ttlMs); this.deps.onLeaseGranted?.(lease); return lease;
  }
  public async execute(id: string, command: TuningCommand): Promise<TuningReceipt> {
    const receipt = this.state.intake(id, command); const key = `${id}:${command.commandId}`;
    if (receipt.stage !== 'accepted' || this.accepted.has(key)) return receipt;
    if (command.action.kind === 'stop') {
      this.accepted.add(key); this.history.append(command.target, 'host', 'receipt', receipt);
      void this.stopCommand(id, command); return receipt;
    }
    if (this.running.has(id) || this.owners.has(id)) return this.rejection(command, 'operation-in-progress');
    this.accepted.add(key); this.running.add(id);
    while (this.accepted.size > 512) this.accepted.delete(this.accepted.values().next().value as string);
    this.history.append(command.target, 'host', 'receipt', receipt);
    void this.apply(id, command); return receipt;
  }
  private async stopCommand(id: string, command: TuningCommand): Promise<void> {
    let receipt: TuningReceipt;
    try {
      await this.revoke(id, 'operator-stop');
      const snapshot = this.state.session(id);
      const target = { ...(snapshot?.target ?? command.target), configRevision: command.expectedRevision + 1 };
      receipt = { version: 1, commandId: command.commandId, target, stage: 'applied', settings: [], policyVersion: 'production-v1' };
      if (snapshot) this.recordTrial({ ...snapshot, target, phase: 'stopped', receipts: [...snapshot.receipts.slice(-127), receipt] });
    } catch (error) { receipt = this.rejection(command, (error as Error).message, 'failed'); }
    this.state.complete(id, receipt); this.history.append(receipt.target, 'host', 'receipt', receipt);
  }
  public acquire(sessionId: string, ownerId: string, deadlineMs: number): boolean {
    const now = this.deps.clock.monotonicMs();
    if (!this.state.lease(sessionId) || this.running.has(sessionId) || this.owners.has(sessionId)
      || !Number.isFinite(deadlineMs) || deadlineMs <= now || deadlineMs > now + 1_500_000) return false;
    const timer = setTimeout(() => { void this.runner?.cancel(ownerId, 'experiment-deadline'); }, deadlineMs - now);
    this.owners.set(sessionId, { id: ownerId, deadlineMs, timer }); return true;
  }
  public release(sessionId: string, ownerId: string): void {
    const owner = this.owners.get(sessionId); if (owner?.id !== ownerId) return;
    clearTimeout(owner.timer); this.owners.delete(sessionId);
  }
  public async executeOwned(command: TuningCommand, ownerId: string): Promise<TuningReceipt> {
    const id = command.target.sessionId; const owner = this.owners.get(id);
    if (owner?.id !== ownerId || owner.deadlineMs <= this.deps.clock.monotonicMs() || this.running.has(id)) {
      return this.rejection(command, 'operation-in-progress');
    }
    const receipt = this.state.intake(id, command);
    if (receipt.stage !== 'accepted') return receipt;
    this.running.add(id); this.history.append(command.target, 'host', 'receipt', receipt);
    return this.apply(id, command);
  }
  public renewQualification(request: TuningExperimentRequest, deadlineMs: number): void {
    if (request.spec.kind !== 'qualification' || this.owners.get(request.sessionId)?.deadlineMs !== deadlineMs
      || !this.hasLease(request.sessionId, request.leaseId)) throw new Error('qualification-renewal-denied');
    const lease = this.state.extendRun(request.sessionId, request.leaseId, deadlineMs);
    this.deps.onLeaseGranted?.(lease);
  }
  public recordExperiment(snapshot: TuningExperimentSnapshot): void {
    const current = this.state.session(snapshot.sessionId); if (current) this.history.append(current.target, 'host', 'experiment', snapshot);
  }
  public async startExperiment(request: TuningExperimentRequest): Promise<TuningExperimentSnapshot> {
    if (!this.runner) throw new Error('experiment-runner-unavailable');
    const lease = this.state.lease(request.sessionId);
    if (!lease || lease.leaseId !== request.leaseId || lease.expiresAtHostMonotonicMs <= this.deps.clock.monotonicMs()) throw new Error('lease-expired');
    if (this.state.authority(request.sessionId)?.configRevision !== request.expectedRevision) throw new Error('stale-revision');
    if (this.running.has(request.sessionId)) throw new Error('operation-in-progress');
    return this.runner.start(request);
  }
  public async experiment(id: string) { return this.runner?.status(id); }
  public async cancelExperiment(id: string, reason: string, authorization?: { requestId: string; sessionId: string; leaseId: string }) {
    if (!this.runner) throw new Error('experiment-runner-unavailable');
    const current = this.runner.status(id);
    if (!current || (authorization && (current.sessionId !== authorization.sessionId
      || !this.hasLease(authorization.sessionId, authorization.leaseId)))) throw new Error('experiment-unauthorized');
    return this.runner.cancel(id, reason);
  }
  public async revoke(id: string, reason: string): Promise<void> {
    const pending = this.revocations.get(id); if (pending) return pending;
    const snapshot = this.state.session(id); if (!snapshot || !this.state.lease(id)) return;
    const cleanup = this.performRevoke(id, reason, snapshot).catch(error => {
      const current = this.state.session(id) ?? snapshot;
      this.recordTrial({ ...current, phase: 'failed', restoration: { cause: 'stop',
        trialId: current.target.trialId, status: 'failed' },
        error: { code: 'production-restoration-failed', message: (error as Error).message.slice(0, 2048) } });
      throw error;
    }).finally(() => this.revocations.delete(id));
    this.revocations.set(id, cleanup); return cleanup;
  }
  private async performRevoke(id: string, reason: string, snapshot: TuningTrialSnapshot): Promise<void> {
    this.state.revoke(id); this.history.append(snapshot.target, 'host', 'lease-revoked', { reason: reason.slice(0, 128) });
    const owner = this.owners.get(id); if (owner) { void this.runner?.cancel(owner.id, reason); this.release(id, owner.id); }
    const fresh = this.sessionFresh.get(id) ?? this.fresh;
    try {
      if (fresh) await fresh.stop(reason);
      else {
        const result = await this.deps.runtime.restore(snapshot.target);
        if (result?.stage === 'failed') throw new Error(result.error?.message ?? 'production-restoration-failed');
      }
    } finally { await this.deps.onLeaseRevoked?.(id, reason); }
  }
  public async dispose(): Promise<void> {
    for (const snapshot of this.state.sessions()) await this.revoke(snapshot.target.sessionId, 'disposed');
    this.state.dispose(); this.history.dispose(); this.listeners.clear(); this.deps.localTransport.dispose();
  }
  private async apply(id: string, command: TuningCommand): Promise<TuningReceipt> {
    try {
      const receipt = await this.deps.commandDispatcher.execute(command);
      this.state.complete(id, receipt); this.history.append(receipt.target, 'host', 'receipt', receipt);
      if (receipt.stage === 'applied') {
        const snapshot = this.state.session(id);
        if (snapshot) this.recordTrial({ ...snapshot, target: receipt.target, receipts: [...snapshot.receipts.slice(-127), receipt] });
        if (command.action.kind === 'stop') this.state.revoke(id);
      }
      return receipt;
    } catch (error) {
      const receipt = this.rejection(command, (error as Error).message, 'failed');
      this.state.complete(id, receipt); this.history.append(command.target, 'host', 'receipt', receipt);
      return receipt;
    } finally { this.running.delete(id); }
  }
  private rejection(command: TuningCommand, code: string, stage: 'rejected' | 'failed' = 'rejected'): TuningReceipt {
    const receipt: TuningReceipt = { version: 1, commandId: command.commandId, target: command.target, stage,
      settings: [], error: { code: code.slice(0, 128), message: code.slice(0, 2048) }, policyVersion: 'tuning-v1' };
    this.state.complete(command.target.sessionId, receipt); return receipt;
  }
}
